import { isInFlight, type Message } from './protocol'

// LF-918: the sidebar's state, as a pure reducer.
//
// Replies arrive as whole-message snapshots from two places that can race —
// cable broadcasts and HTTP transcript fetches — so messages are merged by id,
// keeping the highest revision, and a finished message is never put back into
// a streaming state. "New chat" bumps `generation`; anything still arriving
// for an older generation (a slow response, a late broadcast) is dropped.

export type Notice = { kind: 'error' | 'limit'; text: string }
export type Connection = 'idle' | 'connecting' | 'connected' | 'disconnected'

export type State = {
  generation: number
  conversation: { id: string; streamToken: string } | null
  order: string[]
  messages: Record<string, Message>
  sending: boolean
  notice: Notice | null
  connection: Connection
}

export type Event =
  | { type: 'newChat' }
  | { type: 'sending'; generation: number }
  | {
      type: 'conversationStarted'
      generation: number
      id: string
      streamToken: string
    }
  | {
      type: 'asked'
      generation: number
      question: Message
      reply: Message
    }
  | { type: 'askFailed'; generation: number; notice: Notice }
  | { type: 'messages'; generation: number; messages: Message[] }
  | { type: 'connection'; generation: number; connection: Connection }

export const initialState = (generation = 0): State => ({
  generation,
  conversation: null,
  order: [],
  messages: {},
  sending: false,
  notice: null,
  connection: 'idle',
})

const assertNever = (value: never): never => {
  throw new Error(`Unexpected chatbot event: ${JSON.stringify(value)}`)
}

const isTerminal = (message: Message) => !isInFlight(message)

export const mergeMessage = (state: State, incoming: Message): State => {
  const existing = state.messages[incoming.id]
  if (existing) {
    if (incoming.revision <= existing.revision) return state
    if (isTerminal(existing) && !isTerminal(incoming)) return state
    return {
      ...state,
      messages: { ...state.messages, [incoming.id]: incoming },
    }
  }
  return {
    ...state,
    order: [...state.order, incoming.id],
    messages: { ...state.messages, [incoming.id]: incoming },
  }
}

export const reducer = (state: State, event: Event): State => {
  if (event.type === 'newChat') return initialState(state.generation + 1)
  if (event.generation !== state.generation) return state

  switch (event.type) {
    case 'sending':
      return { ...state, sending: true, notice: null }
    case 'conversationStarted':
      return {
        ...state,
        conversation: { id: event.id, streamToken: event.streamToken },
        connection: 'connecting',
      }
    case 'asked':
      return {
        ...mergeMessage(mergeMessage(state, event.question), event.reply),
        sending: false,
      }
    case 'askFailed':
      return { ...state, sending: false, notice: event.notice }
    case 'messages':
      return event.messages.reduce(mergeMessage, state)
    case 'connection':
      return { ...state, connection: event.connection }
    default:
      return assertNever(event)
  }
}

// Each question followed by its reply. Arrival order alone is not enough: a
// reply's first broadcast can beat the response that carries its question.
export const transcript = (state: State): Message[] => {
  const messages = state.order.map((id) => state.messages[id])
  const replies = new Map(
    messages.flatMap((m) => (m.replyToId ? [[m.replyToId, m] as const] : [])),
  )
  const placed = new Set<string>()
  const ordered: Message[] = []
  for (const message of messages) {
    if (message.role !== 'user') continue
    ordered.push(message)
    placed.add(message.id)
    const reply = replies.get(message.id)
    if (reply) {
      ordered.push(reply)
      placed.add(reply.id)
    }
  }
  return [...ordered, ...messages.filter((m) => !placed.has(m.id))]
}

export const isBusy = (state: State): boolean =>
  state.sending || transcript(state).some(isInFlight)
